import {replace} from '../router';
import {infinityService} from '../services/InfinityClient.service';
import {
    EXPRESS_PATH,
    MEETING_MANAGER_PATH,
    STEP_BY_STEP_PATH,
} from '../constants';
import {logger} from '../logger';
import {config} from '../config';

import {toClientCallType} from './toClientCallTypes';
import {isVideoStreamTrackProcessorAPIs} from '../types';

const decodeURIPart = (part: string) =>
    decodeURIComponent(part.replace(/\+/g, ' '));

const updateConfig: typeof config.set = opt => {
    logger.debug(
        {
            previous: {[opt.key]: config.get(opt.key)},
            next: {[opt.key]: opt.value},
            persist: Boolean(opt.persist),
        },
        'Update config',
    );
    return config.set(opt);
};

/**
 * Parses legacy query parameters and applies them to config/services.
 *
 * @param legacyParams - The query string to parse
 * @param pathAlias - Optional conference alias extracted from the URL path (`/m/<alias>`),
 *   used as a fallback when the `conference` query parameter is not provided
 * @param pathFlow - Optional flow extracted from the URL path (`/m/<alias>/<flow>`),
 *   used as a fallback when the `flow` query parameter is not provided
 *
 * @example
 * // With query param: conference param takes precedence
 * parseLegacyParams('conference=foo&join=1');
 * // With path alias fallback
 * parseLegacyParams('name=Bob&join=1', 'meet.bob');
 * // With path flow fallback
 * parseLegacyParams('callType=none', 'meet.bob', 'step-by-step');
 */
export const parseLegacyParams = (
    legacyParams: string,
    pathAlias?: string,
    pathFlow?: string,
) => {
    try {
        const params = new URLSearchParams(legacyParams);
        const bandwidth = params.get('bandwidth');
        const conference = params.get('conference') ?? pathAlias ?? null;
        const extension = params.get('extension');
        const join = params.get('join');
        const muteCamera = params.get('muteCamera');
        const muteMicrophone = params.get('muteMicrophone');
        const name = decodeURIPart(params.get('name') ?? '');
        const pin = params.get('pin');
        const role = params.get('role');
        const callTag = params.get('callTag');
        const callType = params.get('callType');
        const videoProcessingAPI = params.get('videoProcessingAPI');
        const ssoToken = params.get('ssoToken');
        const token = params.get('token');
        const allowChangeOfMediaDirection = params.get(
            'allowChangeOfMediaDirection',
        );
        const isMeetingManager = params.get('mm');
        const flow = params.get('flow') ?? pathFlow ?? null;

        const isGuest = role === 'guest';
        updateConfig({key: 'isGuest', value: isGuest});

        if (name) {
            updateConfig({key: 'displayName', value: name});
            updateConfig({key: 'skipDisplayNamePrompt', value: true});
        }

        if (bandwidth) {
            updateConfig({key: 'bandwidth', value: bandwidth});
        }

        if (callType) {
            updateConfig({key: 'callType', value: toClientCallType(callType)});
        }

        if (allowChangeOfMediaDirection) {
            if (allowChangeOfMediaDirection === 'true') {
                updateConfig({
                    key: 'allowChangeOfMediaDirection',
                    value: {audio: true, video: true, presentation: true},
                });
            } else {
                updateConfig({
                    key: 'allowChangeOfMediaDirection',
                    value: {audio: false, video: false, presentation: true},
                });
            }
        } else if (callType) {
            // Make it backward compatible to the old behavior
            updateConfig({
                key: 'allowChangeOfMediaDirection',
                value: {audio: false, video: false, presentation: true},
            });
        }

        // If enforceVideoMute is set, we don't want to overwrite it with the legacy params. The actual logic is in useEnforceMuteByDefault.ts which is used in MeetingFlow.page.tsx
        if (muteCamera && !config.get('enforceVideoMute')) {
            updateConfig({
                key: 'isVideoInputMuted',
                value: muteCamera.toLowerCase() === 'true',
            });
        }

        // If enforceVideoMute is set, we don't want to overwrite it with the legacy params. The actual logic is in useEnforceMuteByDefault.ts which is used in MeetingFlow.page.tsx
        if (muteMicrophone && !config.get('enforceAudioMute')) {
            updateConfig({
                key: 'isAudioInputMuted',
                value: muteMicrophone.toLowerCase() === 'true',
            });
        }

        if (isVideoStreamTrackProcessorAPIs(videoProcessingAPI)) {
            updateConfig({
                key: 'videoProcessingAPI',
                value: videoProcessingAPI,
            });
        }

        if (ssoToken) {
            infinityService.setSsoToken(ssoToken ? ssoToken : undefined);
        }

        if (token) {
            infinityService.setToken(token);
        }

        if (conference) {
            let redirect = `/m/${conference}`;
            if (isMeetingManager === 'true' || flow === MEETING_MANAGER_PATH) {
                redirect += `/${MEETING_MANAGER_PATH}`;
            } else if (!join) {
                if (flow === EXPRESS_PATH || flow === STEP_BY_STEP_PATH) {
                    redirect +=
                        flow === EXPRESS_PATH
                            ? `/${EXPRESS_PATH}`
                            : `/${STEP_BY_STEP_PATH}`;
                } else if (isGuest) {
                    redirect += `/${STEP_BY_STEP_PATH}`;
                } else {
                    redirect += `/${EXPRESS_PATH}`;
                }
            }

            const lng = params.get('lng');
            if (lng) {
                // Forward `lng` value so we can get this passed to i18next
                redirect += `?lng=${lng}`;
            }
            const rid = params.get('rid');
            if (rid) {
                redirect += `?rid=${rid}`;
            }
            replace(redirect);
        }

        if (pin) {
            infinityService.setPin(pin);
        }

        if (extension) {
            infinityService.setConferenceExtension(extension);
        }

        if (join === '1') {
            updateConfig({key: 'join', value: true});
        }

        if (callTag) {
            infinityService.setCallTag(callTag);
        }
    } catch (reason) {
        logger.debug({reason}, `Can't parse the legacy params`);
    }
};
